import { useCart } from 'contexts/CartContext/CartContext.hooks';
import { ICartProps, ILineItem } from 'contexts/CartContext/CartContext.types';
import SimpleLineCard from 'pages/Cart/Cards/SimpleLine/SimpleLineCard';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
    ContractCheckbox,
    DeclineAssuranceCheckbox,
    PaymentCheckbox
} from './MonthlyPaymentContract.components';
import useMonthlyPaymentContractStyles from './MonthlyPaymentContract.styles';
import ViaeroButton from '../../../../components/core/ViaeroButton/ViaeroButton';
import { SaveMonthlyContracts } from './MonthlyPaymentContract.api';
import {
    IInstallmentAgreementLineItem,
    IInstallmentAgreementRequest
} from './MonthlyPaymentContract.types';
import { Config } from '../../../../config';

const MonthlyPaymentContract = () => {
    const styles = useMonthlyPaymentContractStyles();

    const { cartStepIndex, cart, updateAgreements, updateCartStepIndex } = useCart();

    const [checkboxCount, setCheckboxCount] = useState(0);
    const [checkedCount, setCheckedCount] = useState(0);

    useEffect(() => {
        const lineItemWithDevice = cart.lineItems.filter((item) => item.device);
        const request: IInstallmentAgreementRequest = {
            id: cart.paymentConfirmationId,
            accountNumber: 53245,
            customerInfo: {
                firstName: cart.customerInfo.firstName,
                lastName: cart.customerInfo.lastName,
                address: cart.customerInfo.address
            },
            lineItems: lineItemWithDevice.map<IInstallmentAgreementLineItem>(
                (x): IInstallmentAgreementLineItem => {
                    const product = x.device?.product;
                    const retail = product?.pricing.retail ?? 0;
                    const downPayment = x.device?.downPayment ?? 0;
                    const numPayments = product?.pricing.numMonths ?? 0;
                    const device = `${product?.baseInfo.brand} ${product?.baseInfo.name}`;

                    return {
                        amountFinanced: retail - downPayment,
                        downPayment: downPayment,
                        numPayments: numPayments,
                        device: device
                    };
                }
            )
        };

        SaveMonthlyContracts(request);

        const agreements = cart.agreements;
        lineItemWithDevice.forEach((x, i) => {
            agreements.contractUrls.push({
                lineNumber: x.lineNumber,
                url: `${Config.url.CART_URL}/data/agreements/${cart.paymentConfirmationId}/${i}.pdf`
            });
        });
        updateAgreements(agreements);
    }, []);

    // Determine how many checkboxes exist so we can validate against them.
    useEffect(() => {
        let sumCheckboxCount = 0;
        cart.lineItems.forEach((lineItem: ILineItem) => {
            // Every line item contains at least one checkbox.
            sumCheckboxCount += 1;

            // Some line items can contain more than one checkbox.
            if (lineItem.device && !lineItem.device.useRetailPrice) sumCheckboxCount += 1;
            if (lineItem.device && !lineItem.device.useAssurance) sumCheckboxCount += 1;
        });

        setCheckboxCount(sumCheckboxCount);
    }, [cart.lineItems]);

    const handleCheckedChange = (checked: boolean) => {
        checked
            ? setCheckedCount((checkedCount) => checkedCount + 1)
            : setCheckedCount((checkedCount) => checkedCount - 1);
    };

    useEffect(() => {
        if (checkedCount !== 0 && checkedCount === checkboxCount) {
            // All checkboxes have been checked.
            setAcceptedMonthlyPaymentContract(true, cart);
            return;
        }

        setAcceptedMonthlyPaymentContract(false, cart);
    }, [checkboxCount, checkedCount]);

    const setAcceptedMonthlyPaymentContract = (isAccepted: boolean, cart: ICartProps) => {
        const agreements = {
            ...cart.agreements,
            acceptedMonthlyPaymentContract: isAccepted
        };

        updateAgreements(agreements);
    };

    const handleClick = () => {
        updateCartStepIndex(cartStepIndex + 1);
    };

    const lineCards: ReactNode[] = useMemo(
        () =>
            cart.lineItems.map((lineItem: ILineItem) => (
                <SimpleLineCard key={`line-card-${lineItem.lineNumber}`} item={lineItem}>
                    <div className={styles.simpleLineCardWrapper}>
                        <PaymentCheckbox onChange={handleCheckedChange} />
                        <DeclineAssuranceCheckbox
                            device={lineItem.device}
                            onChange={handleCheckedChange}
                        />
                        <ContractCheckbox
                            lineNum={lineItem.lineNumber}
                            contractUrls={cart.agreements.contractUrls}
                            device={lineItem.device}
                            onChange={handleCheckedChange}
                        />
                    </div>
                </SimpleLineCard>
            )),
        [cart.lineItems, cart.agreements]
    );

    return (
        <>
            <div>{lineCards.length ? lineCards : <></>}</div>
            <ViaeroButton
                text={'Agree and Continue'}
                click={handleClick}
                disabled={!cart.agreements.acceptedMonthlyPaymentContract}
                className={styles.agreeButton}
            />
        </>
    );
};

export default MonthlyPaymentContract;
